import { z } from "zod";
import { Hotspot, MODEL_LIMITS } from "./scene3d.js";

/** Blocs d'une leçon : ce que le lecteur de leçon (DOM component) sait afficher. */

export const TextBlock = z.object({ type: z.literal("text"), md: z.string() });
export const FormulaBlock = z.object({ type: z.literal("formula"), latex: z.string(), display: z.boolean().default(true) });
export const FigureBlock = z.object({
  type: z.literal("figure"),
  assetId: z.string(),
  engine: z.enum(["mafs", "jsxgraph"]).default("mafs"),
  spec: z.record(z.unknown()), // description de la figure (objets, curseurs, contraintes)
  params: z.record(z.number()).default({}),
});
export const Scene3DBlock = z.object({
  type: z.literal("scene3d"),
  assetId: z.string(),
  sizeBytes: z.number().int(),
  /**
   * Légende : les parties du modèle (nœuds nommés du GLB) sont nommées automatiquement ;
   * la leçon peut renommer, traduire, expliquer ou masquer une partie.
   */
  parts: z.array(z.object({
    id: z.string().max(80),
    label: z.record(z.string().max(60)).default({}),
    text: z.record(z.string().max(300)).optional(),
    hidden: z.boolean().optional(),
  })).max(40).default([]),
  fallback2dAssetId: z.string().nullable().default(null),
  hotspots: z.array(Hotspot).max(MODEL_LIMITS.maxHotspots).default([]),
  /** Crédit du modèle (licence CC BY…), recopié par le serveur depuis la banque à l'enregistrement. */
  credit: z.string().max(300).nullable().optional(),
});
/** Solide généré par le code (spec : voir SolidSpec dans scene3d.ts). */
export const Solid3DBlock = z.object({ type: z.literal("solid3d"), spec: z.record(z.unknown()) });
export const AudioBlock = z.object({ type: z.literal("audio"), assetId: z.string(), lang: z.string(), durationSec: z.number() });
export const VideoBlock = z.object({
  type: z.literal("video"),
  assetId: z.string(),
  kind: z.enum(["TEACHER_INTRO", "REAL_EXPERIMENT", "EXAM_CORRECTION"]),
  durationSec: z.number().int(),
  sizeBytes: z.number().int(),
  optional: z.literal(true).default(true),
});
export const MistakeBlock = z.object({ type: z.literal("mistake"), md: z.string() });
/** Repère posé sur une image (coordonnées relatives : 0 à 1 de gauche à droite et de haut en bas). */
export const ImageMarker = z.object({
  id: z.string().regex(/^[a-z0-9-]{1,40}$/),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  label: z.record(z.string().max(60)).refine((m) => !!m.fr, "Libellé en français obligatoire"),
  text: z.record(z.string().max(300)).default({}),
});
export type ImageMarker = z.infer<typeof ImageMarker>;
/** Schéma ou photo de la médiathèque (anatomie, montage, carte), avec repères numérotés que l'élève touche. */
export const ImageBlock = z.object({
  type: z.literal("image"),
  assetId: z.string(),
  alt: z.record(z.string().max(200)).default({}),
  /** Largeur ÷ hauteur de l'image (mise en page, vidéo) ; mesurée par le studio. */
  aspect: z.number().min(0.2).max(5).default(4 / 3),
  markers: z.array(ImageMarker).max(12).default([]),
  /** Crédit de l'image (licence), recopié par le serveur depuis la médiathèque à l'enregistrement. */
  credit: z.string().max(300).nullable().optional(),
});
/** Figure de géométrie plane (construction élément par élément : voir geometry.ts). */
export const GeometryBlock = z.object({ type: z.literal("geometry"), spec: z.record(z.unknown()), params: z.record(z.number()).default({}) });
/** Question rapide (une touche) glissée entre deux cartes : vérifie la compréhension, ne note pas l'élève. */
export const QuizBlock = z.object({
  type: z.literal("quiz"),
  question: z.string().min(1).max(300),
  choices: z.array(z.object({ id: z.string().min(1).max(20), label: z.string().min(1).max(120) })).min(2).max(4),
  answerId: z.string().min(1).max(20),
  explanation: z.string().max(400).default(""),
});
/** Contrôles de cohérence d'une question (hors schéma : l'union discriminée n'accepte pas de raffinements). */
export function quizIssues(q: z.infer<typeof QuizBlock>): string[] {
  const out: string[] = [];
  if (!q.choices.some((c) => c.id === q.answerId)) out.push("la bonne réponse doit être l'un des choix");
  if (new Set(q.choices.map((c) => c.id)).size !== q.choices.length) out.push("deux choix ont le même identifiant");
  if (q.choices.some((c) => !c.label.trim())) out.push("un choix est vide");
  return out;
}

export const LessonBlock = z.discriminatedUnion("type", [
  TextBlock, FormulaBlock, FigureBlock, Scene3DBlock, Solid3DBlock, AudioBlock, VideoBlock, MistakeBlock, QuizBlock, GeometryBlock, ImageBlock,
]);
export type LessonBlock = z.infer<typeof LessonBlock>;

/** Une étape d'animation : quels blocs sont visibles, quels paramètres de figure, quelle ancre audio. */
export const LessonStep = z.object({
  id: z.string(),
  title: z.record(z.string()), // par langue
  blockIds: z.array(z.string()),
  figureParams: z.record(z.number()).optional(),
  audio: z.object({ assetId: z.string(), startSec: z.number(), endSec: z.number() }).optional(), // narration de la carte
  /** Carte « à retenir » : elle reviendra dans la révision du jour (répétition espacée). */
  recap: z.boolean().optional(),
  /** Vidéo de cours : ce que la voix dit pendant cette étape (une phrase = un temps). Voir video.ts. */
  narration: z.string().max(1500).optional(),
  /** Mise en scène réglée par l'enseignant (« <temps>:<cue> ») ; sinon déduite de la leçon. */
  cues: z.array(z.string().max(120)).max(40).optional(),
});

export type LessonStep = z.infer<typeof LessonStep>;

export const LessonContent = z.object({
  notionId: z.string(),
  lang: z.string(),
  script: z.string().default("Latn"),
  orthography: z.string().nullable().default(null),
  version: z.number().int(),
  validationStatus: z.enum(["AI_DRAFT", "IN_REVIEW", "VALIDATED", "REJECTED"]),
  blocks: z.array(LessonBlock.and(z.object({ id: z.string() }))),
  steps: z.array(LessonStep),
});
export type LessonContent = z.infer<typeof LessonContent>;

// Packs hors ligne : voir packs.ts (document signé servi par GET /packs/:id).
